import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { AlertType, BackButton, Spinner, TextArea, TextInput } from '../../components';
import { AppRoutes, MAX_FORM_WIDTH } from '../../constants';
import { useToast } from '../../hooks';
import { useGetCoachingTemplateQuery } from '../../services';
import { ComponentSize, ScoringAndCoachingTabs } from '../../types';
import ManageCoachingTemplateFooter from './ManageCoachingTemplateFooter';

const MIN_PROMPT_ROWS = 20;

const ManageCoachingTemplatePage = () => {
  const { coachingTemplateId } = useParams();
  const [coachingTemplateName, setCoachingTemplateName] = useState('');
  const [coachingTemplatePrompt, setCoachingTemplatePrompt] = useState('');

  const navigate = useNavigate();
  const { showToast } = useToast();

  const { data: existingCoachingTemplate, isLoading: isCoachingTemplateLoading } = useGetCoachingTemplateQuery(
    { id: coachingTemplateId || '' },
    {
      skip: !coachingTemplateId,
    }
  );

  const navigateToCoachingRubrics = useCallback(
    () => navigate(AppRoutes.SCORING_AND_COACHING + '/' + ScoringAndCoachingTabs.COACHING_RUBRICS, { replace: true }),
    [navigate]
  );

  // Update coaching template name and prompt when coaching template data is fetched
  useEffect(() => {
    if (existingCoachingTemplate) {
      setCoachingTemplateName(existingCoachingTemplate.name);
      setCoachingTemplatePrompt(existingCoachingTemplate.prompt);
    }
  }, [existingCoachingTemplate]);

  // Handle invalid coaching template ID
  useEffect(() => {
    if (coachingTemplateId && !existingCoachingTemplate && !isCoachingTemplateLoading) {
      // If the coaching template ID is provided but the coaching template is not found,
      // navigate to the coaching templates page and show an error toast.
      navigateToCoachingRubrics();
      showToast({
        title: 'Coaching rubric not found',
        message: 'The coaching rubric you are trying to access does not exist.',
        type: AlertType.ERROR,
      });
    }
  }, [coachingTemplateId, existingCoachingTemplate, isCoachingTemplateLoading, navigateToCoachingRubrics, showToast]);

  // If coaching template is loading and there is a coaching template ID, show the loading spinner
  if (isCoachingTemplateLoading && coachingTemplateId) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner size={ComponentSize.MEDIUM} />
      </div>
    );
  }

  return (
    <div className="bg-base-100 flex h-full flex-col">
      <div className="flex h-full w-full flex-col gap-1 overflow-hidden px-8 pt-4">
        <BackButton onBack={navigateToCoachingRubrics} />
        <div className="mx-auto flex h-full w-full flex-col gap-4 overflow-hidden" style={{ maxWidth: MAX_FORM_WIDTH }}>
          <TextInput
            placeholder="Coaching rubric name*"
            value={coachingTemplateName}
            onChange={(e) => setCoachingTemplateName(e.target.value)}
          />
          <div className="display-scrollbar-lg mx-8 flex-1 overflow-auto pb-4">
            <TextArea
              placeholder="Coaching rubric prompt*"
              className="mx-auto"
              value={coachingTemplatePrompt}
              rows={MIN_PROMPT_ROWS}
              fitContent
              onChange={(e) => setCoachingTemplatePrompt(e.target.value)}
            />
          </div>
        </div>
      </div>
      <ManageCoachingTemplateFooter
        existingCoachingTemplate={existingCoachingTemplate}
        coachingTemplateName={coachingTemplateName}
        coachingTemplatePrompt={coachingTemplatePrompt}
        navigateToCoachingRubrics={navigateToCoachingRubrics}
      />
    </div>
  );
};

export default ManageCoachingTemplatePage;
